import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { authBaseUrl } from "@/lib/auth";
import { Button, Card } from "@/components/ui";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Confirm an email address from a verification link.
 *
 * The third route that emails pointed at and that did not exist. Verification is
 * not currently required to sign in (`requireEmailVerification: false`), so this
 * was less damaging than the invitation and reset links — but any verification
 * email sent for any reason landed on a blank page, which reads as a broken
 * product regardless of what it gates.
 *
 * It verifies on mount rather than behind a button: the person already made the
 * decision by clicking the link in their inbox, and asking them to confirm the
 * confirmation is a step with no meaning.
 */
export function VerifyEmail() {
  const { t } = useTranslation("auth");
  const token = new URLSearchParams(window.location.search).get("token");
  const [state, setState] = useState<"working" | "done" | "failed">("working");

  useEffect(() => {
    if (!token) {
      setState("failed");
      return;
    }

    let cancelled = false;

    /*
     * Better Auth verifies email with a plain GET on its own handler rather
     * than a client method, so the request is built here. `redirect=false`
     * keeps it a JSON answer instead of a 302 the page cannot read.
     */
    fetch(`${authBaseUrl}/verify-email?token=${encodeURIComponent(token)}`, {
      credentials: "include",
    })
      .then((response) => {
        if (cancelled) return;
        setState(response.ok ? "done" : "failed");
      })
      .catch(() => {
        if (!cancelled) setState("failed");
      });

    // Strict Mode mounts effects twice in development; without this the second
    // run reports a failure for a token the first run already consumed.
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bg px-5 py-10">
      <Card className="w-full max-w-[400px] p-7 text-center">
        {state === "working" ? (
          <>
            <Loader2 size={22} className="mx-auto mb-3 animate-spin text-text-muted" />
            <p className="text-[14px] text-text-muted">{t("verify.working")}</p>
          </>
        ) : state === "done" ? (
          <>
            <CheckCircle2 size={26} className="mx-auto mb-3 text-success" />
            <h1 className="text-[16px] font-semibold text-text">{t("verify.doneTitle")}</h1>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-text-muted">
              {t("verify.doneBody")}
            </p>
            <Button
              variant="primary"
              className="mt-4 w-full"
              onClick={() => window.location.assign("/")}
            >
              {t("verify.continue")}
            </Button>
          </>
        ) : (
          <>
            <XCircle size={26} className="mx-auto mb-3 text-danger" />
            <h1 className="text-[16px] font-semibold text-text">
              {t("verify.failedTitle")}
            </h1>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-text-muted">
              {t("verify.failedBody")}
            </p>
            <Button
              variant="secondary"
              className="mt-4 w-full"
              onClick={() => window.location.assign("/")}
            >
              {t("verify.goToApp")}
            </Button>
          </>
        )}
      </Card>
      <div className="mt-5">
        <LanguageSwitcher variant="select" />
      </div>
    </div>
  );
}
